import { Link } from "react-router-dom";
import { Check, ArrowRight, Sparkles } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

type FooterLink = { label: string; to: string };

const NEWS_LINKS: FooterLink[] = [
  { label: "Latest News", to: "/latest" },
  { label: "The Daily Brief", to: "/briefs" },
  { label: "Crypto", to: "/crypto" },
  { label: "DeFi", to: "/defi" },
  { label: "Policy", to: "/policy" },
  { label: "Web3", to: "/web3" },
  { label: "Blockchain", to: "/blockchain" },
  { label: "Explainers", to: "/explainers" },
];

const MARKET_LINKS: FooterLink[] = [
  { label: "Market News", to: "/markets" },
  { label: "Layer Index", to: "/layer-index" },
  { label: "Live Charts", to: "/coin/BTC" },
  { label: "DeFi Protocols", to: "/defi/protocols" },
  { label: "Regulation Tracker", to: "/regulation-tracker" },
  { label: "Regulation Radar", to: "/radar" },
];

const COMPANY_LINKS: FooterLink[] = [
  { label: "About NewsLayer", to: "/about" },
  { label: "Advertise", to: "/advertise" },
  { label: "PR Publication", to: "/pr" },
  { label: "Become an Author", to: "/author-program" },
  { label: "Our Authors", to: "/authors" },
];

const RESOURCE_LINKS: FooterLink[] = [
  { label: "Research", to: "/research" },
  { label: "NewsLayer Originals", to: "/originals" },
  { label: "My Feed", to: "/my-feed" },
  { label: "Search", to: "/search" },
  { label: "AI Sector", to: "/ai" },
  { label: "Quantum Sector", to: "/quantum" },
];

const NavLinkItem = ({ link }: { link: FooterLink }) => (
  <li>
    <Link
      to={link.to}
      data-testid={`footer-link-${link.to.replace(/\//g, "-").replace(/^-/, "")}`}
      className="group inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors duration-200"
    >
      <span className="h-px w-0 bg-[#00E5FF] transition-all duration-200 group-hover:w-2.5" aria-hidden="true" />
      <span className="transition-transform duration-200 group-hover:translate-x-0.5">{link.label}</span>
    </Link>
  </li>
);

const NavColumn = ({ title, accent, links, extra, testid }: {
  title: string; accent: string; links: FooterLink[]; extra?: React.ReactNode; testid: string;
}) => (
  <div data-testid={testid}>
    <div className="flex items-center gap-2 mb-5">
      <span className="h-1.5 w-1.5 rotate-45" style={{ backgroundColor: accent }} aria-hidden="true" />
      <h3 className="font-mono text-[11px] uppercase tracking-[0.22em] text-gray-500">{title}</h3>
    </div>
    <ul className="space-y-2.5">
      {links.map((l) => <NavLinkItem key={l.to} link={l} />)}
      {extra}
    </ul>
  </div>
);

const SocialChip = ({ href, label, testid, children }: {
  href: string; label: string; testid: string; children: React.ReactNode;
}) => (
  <a
    href={href} target="_blank" rel="noopener noreferrer" data-testid={testid}
    className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.02] px-4 py-2 text-sm font-medium text-gray-300 transition-colors duration-200 hover:border-[#00E5FF]/50 hover:text-white"
  >
    {children}
    {label}
  </a>
);

const PERKS = ["Premium Intelligence briefings", "Ad-free reading experience", "Members-only research & data"];

const PremiumCard = () => {
  const { user } = useAuth();
  const isPremium = !!user?.is_premium;
  return (
    <div
      data-testid="footer-premium-card"
      className="relative overflow-hidden rounded-xl border border-[#7C3AED]/30 bg-black p-6"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-[#7C3AED] via-[#a855f7] to-[#00E5FF]" aria-hidden="true" />
      <div className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[#7C3AED]/15 blur-3xl" aria-hidden="true" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)", backgroundSize: "18px 18px" }}
        aria-hidden="true"
      />
      <div className="relative">
        <div className="flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-[#00E5FF]" aria-hidden="true" />
          <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#00E5FF]">
            {isPremium ? "Membership active" : "NewsLayer Premium"}
          </p>
        </div>
        <h3 className="mt-3 font-heading text-xl font-bold text-white leading-snug">
          {isPremium ? "You're a member." : "Read the full layer."}
        </h3>
        <p className="mt-2 text-sm text-gray-400 leading-relaxed">
          {isPremium
            ? "Your Premium Intelligence access is live. Manage your plan or dive into members-only research."
            : "Unlock premium intelligence, original research and an ad-free reading experience."}
        </p>
        {!isPremium && (
          <ul className="mt-4 space-y-2">
            {PERKS.map((p) => (
              <li key={p} className="flex items-start gap-2 text-[13px] text-gray-300">
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#7C3AED]" aria-hidden="true" />
                {p}
              </li>
            ))}
          </ul>
        )}
        <Link
          to={isPremium ? "/account" : "/premium"}
          data-testid="footer-premium-cta"
          className="group mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#00E5FF]/40 bg-[#7C3AED]/15 px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#7C3AED]/30 hover:border-[#00E5FF]/70"
        >
          {isPremium ? "Manage membership" : "Go Premium"}
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
        </Link>
        {isPremium && (
          <Link
            to="/premium"
            data-testid="footer-premium-secondary"
            className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-[#00E5FF] transition-colors"
          >
            Explore Premium Intelligence →
          </Link>
        )}
      </div>
    </div>
  );
};

const SeparatorStroke = () => (
  <div className="relative" aria-hidden="true">
    <div className="pointer-events-none absolute left-1/2 top-0 h-10 w-[520px] max-w-[80vw] -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-[#7C3AED]/25 via-[#a855f7]/20 to-[#00E5FF]/25 blur-2xl opacity-60" />
    <div className="relative h-px">
      <div className="absolute inset-y-0 left-0 right-1/2 mr-5 bg-gradient-to-r from-transparent via-[#7C3AED]/40 to-[#7C3AED]/80" />
      <div className="absolute inset-y-0 left-1/2 right-0 ml-5 bg-gradient-to-r from-[#00E5FF]/80 via-[#00E5FF]/40 to-transparent" />
      <span className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rotate-45 border border-[#a855f7] bg-[#050506] shadow-[0_0_14px_rgba(124,58,237,0.9)]" />
      <span className="absolute left-1/2 top-1/2 h-px w-2 -translate-y-1/2 -translate-x-[22px] bg-[#7C3AED]" />
      <span className="absolute left-1/2 top-1/2 h-px w-2 -translate-y-1/2 translate-x-[14px] bg-[#00E5FF]" />
    </div>
  </div>
);

export const Footer = () => {
  const { user } = useAuth();
  const accountLinks: React.ReactNode = user ? (
    <NavLinkItem link={{ label: "My Profile", to: "/account" }} />
  ) : (
    <>
      <NavLinkItem link={{ label: "Create Account", to: "/signup" }} />
      <NavLinkItem link={{ label: "Sign in", to: "/login" }} />
    </>
  );

  return (
    <footer data-testid="footer-root" className="mt-20 bg-[#050506]">
      <SeparatorStroke />
      <div className="relative overflow-hidden">
        <div className="pointer-events-none absolute -bottom-40 left-1/2 h-72 w-[900px] -translate-x-1/2 rounded-full bg-[#7C3AED]/[0.05] blur-3xl" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 lg:py-16">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-3" data-testid="footer-brand-column">
              <Logo />
              <p className="mt-5 max-w-sm text-sm leading-relaxed text-gray-500">
                The front page of the onchain economy. Crypto, Web3 and regulation
                intelligence — live prices, original research and policy tracking in one layer.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <SocialChip href="https://x.com/newslayerapp" label="Follow on X" testid="footer-x-link">
                  <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </SocialChip>
                <SocialChip href="https://t.me/newslayerapp" label="Telegram" testid="footer-telegram-link">
                  <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                  </svg>
                </SocialChip>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-4 md:gap-8 lg:col-span-6">
              <NavColumn title="News" accent="#7C3AED" links={NEWS_LINKS} testid="footer-col-news" />
              <NavColumn title="Markets" accent="#00E5FF" links={MARKET_LINKS} testid="footer-col-markets" />
              <NavColumn title="Company" accent="#34d399" links={COMPANY_LINKS} extra={accountLinks} testid="footer-col-company" />
              <NavColumn title="Resources" accent="#60a5fa" links={RESOURCE_LINKS} testid="footer-col-resources" />
            </div>

            <div className="lg:col-span-3">
              <PremiumCard />
            </div>
          </div>
        </div>

        <div className="relative border-t border-white/5">
          <div className="max-w-7xl mx-auto flex flex-col items-center gap-3 px-4 py-5 sm:px-6 md:flex-row md:justify-between">
            <p className="font-mono text-xs text-gray-600" data-testid="footer-copyright">
              © {new Date().getFullYear()} NewsLayer.com — The front page of the onchain economy
            </p>
            <div className="flex items-center gap-2 font-mono text-xs text-gray-600">
              <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-[#00E5FF] transition-colors">Privacy Policy</Link>
              <span className="text-gray-700">·</span>
              <Link to="/terms" data-testid="footer-terms-link" className="hover:text-[#00E5FF] transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
